import { useTranslation } from 'react-i18next';
import { AccountBalance, AccountParentAccountInfo } from 'vopay';
import AccountBreadcrumbs from '@/components/Entities/Accounts/AccountBreadcrumbs/AccountBreadcrumbs';
import ActiveIcon from '@/components/Accounts/Account/svg/Active.svg';
import PendingIcon from '@/components/Accounts/Account/svg/Pending.svg';
import EntityHeaderCard from './EntityHeaderCard';
import type { AccountCredentials } from './accountCredentials';

type AccountCardProps = {
  account: AccountBalance;
  /** Null hides the credentials strip — the caller decides who may see it and when (Active only). */
  credentials: AccountCredentials | null;
  /**
   * Draws the account's ancestors as a trail above its name, each a route to that parent; for a
   * sub-account on the account page. Omitted (the drawer, a top-level account), no trail is drawn.
   */
  onParentAccountClick?: (parentAccount: AccountParentAccountInfo) => void;
};

/**
 * The account's identity card (spec 5.5): name, currency and status, with the API credentials strip
 * along the bottom when the caller passes credentials. Shared by the details drawer and the full
 * account page, which render the same card in their headers.
 */
export default function AccountCard({ account, credentials, onParentAccountClick }: AccountCardProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AccountHeaderCards.AccountCard' });

  return (
    <EntityHeaderCard
      data-testid="accountCard"
      icon={account.IsActive ? ActiveIcon : PendingIcon}
      iconAlt={t(account.IsActive ? 'iconAltActive' : 'iconAltPending', {
        defaultValue: account.IsActive ? 'Active account' : 'Pending account',
      })}
      aboveName={
        onParentAccountClick &&
        account.ParentAccounts.length > 0 && (
          <AccountBreadcrumbs parentAccounts={account.ParentAccounts} onCrumbClick={onParentAccountClick} />
        )
      }
      // Figma: H3/Large Title 24/Emphasized over Body/16/Regular
      name={account.AccountName}
      nameLevel="3"
      subtitle={account.Currency}
      status={account}
      credentials={credentials}
    />
  );
}
